import React from "react";

import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { CREATE_DOCUMENT_COUNSEL_MODAL_NAME } from "components/modals/documents/CreateDocumentCounselModal";
import { CREATE_DOCUMENT_COURT_MODAL_NAME } from "components/modals/documents/CreateDocumentCourtModal";

import useModal from "hooks/useModal";
import usePartyLabels from "hooks/usePartyLabels";

import {
  isCounselPath,
  isCourtPath,
  routeForBasePath,
} from "utils/normalizedPaths/routeForBasePath";

import CLIENT from "@gql/queries/client/client";

const PercentPill = ({ value }) => (
  <span
    style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 12,
      background: value === 100 ? "#e6ffed" : "#e6f0ff",
      color: "#1f2328",
      fontSize: 12,
      fontWeight: 700,
    }}
  >
    {value}%
  </span>
);

const TemplateCompletenessPanel = ({
  clientId,
  summary,
  groups,
  partyLabels,
  onExpand,
  onCollapse,
  // Optional: collapsed-missing wizard stats & steps from CreateCourtFormPage
  wizardStats,
  wizardMissingSteps,
  // When true, panel is rendered within a modal; disable expanding and show a compact overflow hint
  inModal = false,

  onWizardOpenPath,
  // Note: onWizardOpenPath prop removed - we now scroll directly to sections
  // Optional: override the title label (defaults to Form Completeness)
  title = "Form Completeness",
}) => {
  const navigate = useNavigate();
  const { open: openCounsel } = useModal(CREATE_DOCUMENT_COUNSEL_MODAL_NAME);
  const { open: openCourt } = useModal(CREATE_DOCUMENT_COURT_MODAL_NAME);

  // Ensure we always use the up-to-date file labels as fallback
  const { data: clientData } = useQuery(CLIENT, {
    variables: { where: { id: clientId } },
    skip: !clientId,
    fetchPolicy: "cache-and-network",
  });

  // Function to scroll to a section without starting the wizard
  const scrollToSectionOnly = React.useCallback((sectionPath) => {
    try {
      if (!sectionPath || typeof sectionPath !== "string") return;
      const container = document.querySelector(".dynamic-form-editor");
      if (!container) return;

      // Find the target section
      const targetContent = container.querySelector(`[data-base-path="${sectionPath}"]`);

      if (targetContent) {
        // Open the target section if it's closed
        const isClosed = targetContent.getAttribute("data-state") === "closed";
        if (isClosed) {
          const trigger = targetContent.previousElementSibling;
          if (trigger && typeof trigger.click === "function") trigger.click();
        }

        setTimeout(() => {
          const trigger = targetContent.previousElementSibling;
          if (trigger && typeof trigger.scrollIntoView === "function") {
            trigger.scrollIntoView({ behavior: "smooth", block: "center" });
          } else if (targetContent && typeof targetContent.scrollIntoView === "function") {
            targetContent.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 80);
      }
    } catch (error) {
      console.error("scrollToSectionOnly error:", error);
    }
  }, []);

  const handleOpen = (basePath) => {
    // If a delegate callback is provided (e.g., from a modal), invoke it and exit early
    if (typeof onWizardOpenPath === "function") {
      onWizardOpenPath(basePath);
      return;
    }
    // First try to scroll to the section without starting wizard
    if (basePath) {
      scrollToSectionOnly(basePath);
      return;
    }

    // Fallback to original behavior for special cases
    if (isCounselPath(basePath)) return openCounsel({ client: { id: clientId } });
    if (isCourtPath(basePath)) return openCourt({ client: { id: clientId } });
    const to = routeForBasePath(basePath, clientId);
    if (to) navigate(to);
  };

  const handleOpenField = (fullPath, fallbackBasePath) => {
    if (typeof onWizardOpenPath === "function") {
      // Prefer fullPath, fall back to basePath if fullPath absent
      onWizardOpenPath(fullPath || fallbackBasePath);
      return;
    }
    const targetPath = fallbackBasePath || fullPath;
    if (targetPath) {
      scrollToSectionOnly(targetPath);
      return;
    }
    return handleOpen(fallbackBasePath || fullPath);
  };

  const percent = wizardStats?.percent ?? summary?.presentCompletenessPercent ?? 100;
  // Keep panel visible at 100% to show success/help message

  const isProfile = title?.includes("Profile");

  const tip =
    percent < 50
      ? isProfile
        ? "Many details are missing. Complete the profile wizard below first."
        : "Many details are missing. Complete the form wizard (below) first."
      : percent < 80
      ? isProfile
        ? "Tip: Try adding this information using the profile wizard below."
        : "Tip: Try adding this information using the form wizard below."
      : null;

  const [expanded, setExpanded] = React.useState(false);
  // Track which category rows are expanded
  const [openGroups, setOpenGroups] = React.useState(() => new Set());
  // Dedupe steps/groups by basePath (or path) to avoid duplicate keys/rows
  const normalizedSteps = React.useMemo(() => {
    const list =
      wizardMissingSteps && wizardMissingSteps.length ? wizardMissingSteps : groups || [];
    const seen = new Set();
    const out = [];
    (list || []).forEach((g) => {
      const base = g?.basePath || g?.path || g?.label;
      const key = String(base || "unknown");
      if (seen.has(key)) return;
      seen.add(key);
      out.push(g);
    });
    return out;
  }, [wizardMissingSteps, groups]);

  const groupsToShow = normalizedSteps; // show all categories collapsed by default
  const totalGroupsCount = normalizedSteps.length;

  // Build a quick lookup of missing fields by basePath so we can show field-level links
  const fieldsByBasePath = React.useMemo(() => {
    const map = new Map();
    (groups || []).forEach((g) => {
      const base = g?.basePath || g?.path || g?.label || "";
      const list = [];
      (g?.missingFields || []).forEach((f) => {
        if (f?.fullPath) list.push({ label: f?.label || f.fullPath, fullPath: f.fullPath });
      });
      (g?.missingInArrays || []).forEach((arr) => {
        (arr?.missingFields || []).forEach((f) => {
          if (f?.fullPath) list.push({ label: f?.label || f.fullPath, fullPath: f.fullPath });
        });
      });
      if (!map.has(base)) map.set(base, list);
    });
    return map;
  }, [groups]);

  // Try to infer the selected label set (FIRSTNAME/LASTNAME/PARENT/etc.) from current file labels
  const labelOptions = usePartyLabels({ clientId });
  const inferred = React.useMemo(() => {
    if (!labelOptions || !labelOptions.length) return {};
    const byClient = labelOptions.find((o) => o.clientLabel === clientData?.client?.clientLabel);
    const byEx = labelOptions.find((o) => o.exLabel === clientData?.client?.exLabel);
    const match = byClient || byEx;
    return match ? { client: match.clientLabel, ex: match.exLabel, party: match.party } : {};
  }, [labelOptions, clientData?.client?.clientLabel, clientData?.client?.exLabel]);

  const clientPartyLabel =
    inferred.client || clientData?.client?.clientLabel || partyLabels?.clientLabel || "Client";
  const exPartyLabel =
    inferred.ex || clientData?.client?.exLabel || partyLabels?.exLabel || "Opposing Party";

  const prettifyGroupLabel = React.useCallback(
    (group) => {
      const basePathRaw = group?.basePath || "";
      const original = group?.label || "";

      // Prefer explicit category if provided by API
      const category = (group?.category || "").toString().toUpperCase();
      const isExByCategory =
        category.includes("EX") || category.includes("OPPOSING") || category.includes("SPOUSE");
      // Fallback heuristic: ex groups include properties like exAddress, exProfile, exLawyer...
      const isExHeuristic =
        /(^|\.)ex(\.|[A-Z]|\b)/i.test(basePathRaw) ||
        /^\s*(ex|opposing\s*party|client\s+ex)\b/i.test(original);
      const isEx = isExByCategory || isExHeuristic;

      // Remove generic prefixes from the label to avoid duplication with our party label
      let remainder = original
        .replace(/^\s*(Client|Ex|Opposing\s*Party|Former\s*Spouse|Spouse|Partner)\s*/i, "")
        .replace(/^Ex(?=[A-Z])/i, "") // ExAddress -> Address
        .replace(/^Ex\s+/i, "") // Ex Address -> Address
        .replace(/^ExLawyer/i, "Lawyer");

      // Special case: if original label is just "ex" (or variants), drop the remainder entirely
      if (/^\s*(ex|opposing\s*party|spouse|former\s*spouse|partner)\s*$/i.test(original)) {
        remainder = "";
      }

      const prefix = isEx ? exPartyLabel : clientPartyLabel;
      return `${prefix} ${remainder}`.trim();
    },
    [clientPartyLabel, exPartyLabel],
  );

  return (
    <div
      className="template-completeness mt-3"
      style={{ border: "1px solid #dee2e6", borderRadius: 6, padding: 10 }}
    >
      <div className="d-flex align-items-center mb-1" style={{ gap: 8 }}>
        <strong style={{ fontSize: 13 }}>{title}</strong>
        <PercentPill value={percent} />
        {wizardStats ? (
          <span style={{ fontSize: 12, color: "#6c757d" }}>
            {wizardStats.remaining}/{wizardStats.total} missing
          </span>
        ) : (
          summary?.presentTotalCount != null && (
            <span style={{ fontSize: 12, color: "#6c757d" }}>
              {summary.presentMissingCount}/{summary.presentTotalCount} missing
            </span>
          )
        )}
      </div>
      {/* micro bar */}
      <div
        style={{
          height: 4,
          background: "#f1f3f5",
          borderRadius: 2,
          overflow: "hidden",
          marginBottom: 8,
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: "100%",
            background: percent === 100 ? "#28a745" : "#0d6efd",
          }}
        />
      </div>
      {percent === 100 ? (
        <div
          style={{
            fontSize: 12,
            color: "#155724",
            background: "#e6ffed",
            border: "1px solid #c3e6cb",
            borderRadius: 6,
            padding: "6px 8px",
            marginBottom: 6,
            lineHeight: 1.3,
          }}
        >
          <strong>All set!</strong> All required information has been provided.
          {isProfile
            ? " Information will be merged into documents, calculations, and more."
            : " You can create this form now, or edit the provided information below."}
        </div>
      ) : (
        tip && (
          <div
            style={{
              fontSize: 12,
              color: percent < 50 ? "#856404" : "#495057",
              marginBottom: 6,
              lineHeight: 1.3,
            }}
          >
            {tip}
          </div>
        )
      )}

      {percent !== 100 && (
        <React.Fragment>
          <div
            style={{
              fontSize: 13,
              maxHeight: inModal ? 180 : 280,
              overflowY: "auto",
              paddingRight: 4,
              marginBottom: 4,
            }}
          >
            {groupsToShow.map((g) => {
              // Try to get the most specific path available
              const base = g.basePath || g.path || g.label;
              const specificPath = g.specificPath || g.fullPath || g.path || base;
              const fields = fieldsByBasePath.get(base) || [];
              const isOpen = openGroups.has(base);
              const toggleOpen = () => {
                setOpenGroups((prev) => {
                  const next = new Set(prev);
                  if (next.has(base)) next.delete(base);
                  else next.add(base);
                  return next;
                });
              };

              const handleSectionClick = () => {
                if (fields.length === 0) {
                  handleOpen(specificPath);
                } else {
                  toggleOpen();
                }
              };

              return (
                <div key={String(base || "unknown")} style={{ marginBottom: 6 }}>
                  <div
                    className="d-flex align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={handleSectionClick}
                    role="button"
                    tabIndex={0}
                    {...(fields.length > 0 && { "aria-expanded": isOpen })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSectionClick();
                      }
                    }}
                  >
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 12,
                        background: g.allMissing ? "#dc3545" : "#fd7e14",
                        marginRight: 6,
                      }}
                    />
                    <strong style={{ flex: 1 }}>{prettifyGroupLabel(g)}</strong>
                    {fields.length > 0 ? (
                      <span
                        style={{
                          transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "transform .2s",
                        }}
                      >
                        ▶
                      </span>
                    ) : (
                      <span
                        style={{
                          fontSize: 12,
                          color: "#6c757d",
                          marginLeft: 8,
                        }}
                      >
                        →
                      </span>
                    )}
                  </div>
                  {isOpen && fields.length > 0 && (
                    <div
                      style={{ maxHeight: 120, overflowY: "auto", paddingLeft: 18, marginTop: 4 }}
                    >
                      {fields.map((f, idx) => (
                        <div key={f.fullPath || idx} style={{ marginBottom: 4 }}>
                          <button
                            type="button"
                            className="document-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenField(f.fullPath, base);
                            }}
                            style={{
                              background: "none",
                              border: 0,
                              padding: 0,
                              cursor: "pointer",
                              fontSize: 12,
                            }}
                          >
                            {f.label}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {totalGroupsCount > 0 && !inModal && (
            <div style={{ fontSize: 12, textAlign: "right" }}>
              <button
                type="button"
                className="document-link"
                onClick={() => {
                  if (!expanded) {
                    if (typeof onExpand === "function") onExpand();
                    setExpanded(true);
                    setOpenGroups(
                      new Set(normalizedSteps.map((g) => g.basePath || g.path || g.label)),
                    );
                  } else {
                    if (typeof onCollapse === "function") onCollapse();
                    setExpanded(false);
                    setOpenGroups(new Set());
                  }
                }}
                style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }}
              >
                {expanded ? "Show less" : "Show all"}
              </button>
            </div>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default TemplateCompletenessPanel;
